import { useId, useState } from 'react';
import type { AnyEvent } from '@intentionalist/shared';
import { createInvestment } from './mutations.js';

/**
 * Offers an investment entry for an item just marked Invest (FR-8.3).
 *
 * Shown as a banner rather than a modal, so whatever is behind it keeps
 * working: in a review the next item is already on screen, and on an item's own
 * page the rest of the item is still there. Ignoring the offer is a legitimate
 * answer, and a modal would not let it be one.
 *
 * Shared by the review session and the verdict panel because the requirement is
 * about the act of marking, not about the screen it was marked on. It was local
 * to the session, so recording Invest anywhere else silently did not offer --
 * which was true of the verdict panel before this moved.
 *
 * The new entry is linked to the item it would replace, which is the whole
 * reason for making it here rather than from the investment screen.
 */
export function InvestPrompt({
  subject,
  onWrite,
  onDone,
}: {
  subject: { itemId: string; name: string };
  onWrite: (events: AnyEvent[]) => void;
  onDone: () => void;
}) {
  const id = useId();
  const [name, setName] = useState(subject.name);

  return (
    <div className="field" role="status">
      <p>
        <strong>{subject.name === '' ? 'That item' : subject.name}</strong> is marked Invest. Add it
        to the investment list?
      </p>
      <label htmlFor={id}>What you need instead</label>
      <input id={id} value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
      <div className="actions">
        <button
          type="button"
          className="primary"
          disabled={name.trim() === ''}
          onClick={() => {
            onWrite(createInvestment({ name }, [subject.itemId]).events);
            onDone();
          }}
        >
          Add entry
        </button>
        <button type="button" onClick={onDone}>
          Not now
        </button>
      </div>
    </div>
  );
}
